import { type RefObject, useRef } from "react";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import BiotechRoundedIcon from "@mui/icons-material/BiotechRounded";
import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";
import DeviceHubRoundedIcon from "@mui/icons-material/DeviceHubRounded";
import LanRoundedIcon from "@mui/icons-material/LanRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import SchemaRoundedIcon from "@mui/icons-material/SchemaRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import type { SvgIconComponent } from "@mui/icons-material";

type SectionRef = RefObject<HTMLDivElement | null>;

type Workflow = {
  title: string;
  description: string;
  icon: SvgIconComponent;
};

type CapabilityGroup = {
  title: string;
  icon: SvgIconComponent;
  items: string[];
};

type Audience = {
  title: string;
  description: string;
  icon: SvgIconComponent;
};

const primaryButtonSx = {
  textTransform: "none",
  background: "linear-gradient(135deg, #38bdf8 0%, #4f46e5 100%)",
  boxShadow: "0 18px 40px rgba(14, 116, 144, 0.22)",
};

const secondaryDarkButtonSx = {
  textTransform: "none",
  borderColor: "rgba(148,163,184,0.35)",
  color: "#e2e8f0",
  backgroundColor: "rgba(15,23,42,0.28)",
};

const HeroWorkflowGraphic = () => (
  <Box className="rounded-[34px] border border-white/10 bg-slate-950/85 p-6 shadow-2xl shadow-slate-950/50 backdrop-blur">
    <svg
      viewBox="0 0 760 420"
      className="h-full w-full"
      role="img"
      aria-label="Workflow from data to biological insight"
    >
      <defs>
        <linearGradient id="hero-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#111827" />
        </linearGradient>
        <linearGradient id="hero-line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
        <filter id="hero-glow">
          <feGaussianBlur stdDeviation="8" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect x="0" y="0" width="760" height="420" rx="28" fill="url(#hero-bg)" />
      <circle cx="120" cy="84" r="56" fill="rgba(56,189,248,0.08)" />
      <circle cx="644" cy="88" r="64" fill="rgba(99,102,241,0.10)" />

      <path
        d="M122 214 C200 214, 216 146, 294 146 S388 278, 470 278 S560 214, 642 214"
        stroke="url(#hero-line)"
        strokeWidth="4"
        fill="none"
        filter="url(#hero-glow)"
        strokeLinecap="round"
      />

      {[
        { x: 72, y: 182, title: "Data", subtitle: "omics layers" },
        { x: 198, y: 114, title: "Preprocessing", subtitle: "harmonization" },
        { x: 330, y: 114, title: "Integration", subtitle: "shared context" },
        { x: 458, y: 246, title: "Network Inference", subtitle: "relationships" },
        { x: 590, y: 182, title: "Biological Insight", subtitle: "interpretation" },
      ].map((node) => (
        <g key={node.title}>
          <rect
            x={node.x}
            y={node.y}
            width="112"
            height="74"
            rx="18"
            fill="rgba(15,23,42,0.88)"
            stroke="rgba(148,163,184,0.18)"
          />
          <circle
            cx={node.x + 18}
            cy={node.y + 18}
            r="5"
            fill="#67e8f9"
            filter="url(#hero-glow)"
          />
          <text
            x={node.x + 18}
            y={node.y + 40}
            fill="#f8fafc"
            fontSize="15"
            fontWeight="600"
            fontFamily="Arial, sans-serif"
          >
            {node.title}
          </text>
          <text
            x={node.x + 18}
            y={node.y + 60}
            fill="#94a3b8"
            fontSize="12"
            fontFamily="Arial, sans-serif"
          >
            {node.subtitle}
          </text>
        </g>
      ))}
    </svg>
  </Box>
);

const ScientificStrengthsGraphic = () => (
  <Box className="rounded-[34px] border border-white/10 bg-white/5 p-6 shadow-2xl shadow-black/10 backdrop-blur">
    <svg
      viewBox="0 0 620 360"
      className="h-full w-full"
      role="img"
      aria-label="Omics layers connected to networks, pathways, and biological insight"
    >
      <defs>
        <linearGradient id="strengths-line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="620" height="360" rx="28" fill="rgba(15,23,42,0.2)" />

      {[
        { y: 68, label: "Transcriptomics" },
        { y: 118, label: "Proteomics" },
        { y: 168, label: "Metabolomics" },
        { y: 218, label: "Epigenomics" },
      ].map((layer, index) => (
        <g key={layer.label}>
          <rect
            x="34"
            y={layer.y}
            width="180"
            height="34"
            rx="12"
            fill={index % 2 === 0 ? "rgba(255,255,255,0.07)" : "rgba(99,102,241,0.10)"}
            stroke="rgba(148,163,184,0.16)"
          />
          <text
            x="52"
            y={layer.y + 22}
            fill="#e2e8f0"
            fontSize="13"
            fontWeight="600"
            fontFamily="Arial, sans-serif"
          >
            {layer.label}
          </text>
        </g>
      ))}

      <g stroke="url(#strengths-line)" strokeWidth="2.2" fill="none" opacity="0.9">
        <path d="M214 85 C258 85, 268 98, 314 122" />
        <path d="M214 135 C264 135, 274 140, 314 152" />
        <path d="M214 185 C260 185, 274 176, 320 170" />
        <path d="M214 235 C264 235, 280 210, 324 186" />
      </g>

      {[
        { cx: 352, cy: 118, r: 16 },
        { cx: 390, cy: 150, r: 12 },
        { cx: 346, cy: 184, r: 11 },
        { cx: 404, cy: 196, r: 16 },
        { cx: 440, cy: 156, r: 10 },
      ].map((node) => (
        <circle
          key={`${node.cx}-${node.cy}`}
          cx={node.cx}
          cy={node.cy}
          r={node.r}
          fill="rgba(15,23,42,0.92)"
          stroke="rgba(56,189,248,0.42)"
        />
      ))}

      <path
        d="M352 118 L390 150 L346 184 L404 196 L440 156 L390 150"
        stroke="rgba(148,163,184,0.55)"
        strokeWidth="2"
        fill="none"
      />

      <rect
        x="468"
        y="106"
        width="106"
        height="38"
        rx="14"
        fill="rgba(255,255,255,0.06)"
        stroke="rgba(148,163,184,0.16)"
      />
      <rect
        x="468"
        y="158"
        width="106"
        height="38"
        rx="14"
        fill="rgba(255,255,255,0.06)"
        stroke="rgba(148,163,184,0.16)"
      />
      <rect
        x="468"
        y="210"
        width="106"
        height="48"
        rx="16"
        fill="rgba(56,189,248,0.10)"
        stroke="rgba(56,189,248,0.30)"
      />

      <path d="M420 156 H468" stroke="url(#strengths-line)" strokeWidth="2.5" />
      <path d="M420 196 H468" stroke="url(#strengths-line)" strokeWidth="2.5" />
      <path d="M522 196 V210" stroke="url(#strengths-line)" strokeWidth="2.5" />

      <text x="484" y="130" fill="#e2e8f0" fontSize="13" fontWeight="600" fontFamily="Arial, sans-serif">
        Network
      </text>
      <text x="484" y="182" fill="#e2e8f0" fontSize="13" fontWeight="600" fontFamily="Arial, sans-serif">
        Pathways
      </text>
      <text x="484" y="239" fill="#f8fafc" fontSize="13" fontWeight="700" fontFamily="Arial, sans-serif">
        Insight
      </text>
    </svg>
  </Box>
);

const BottleneckMiniFlow = () => (
  <Box className="rounded-[30px] border border-white/10 bg-white/5 p-6 shadow-xl shadow-black/10">
    <Box className="grid gap-5">
      <Box className="rounded-[24px] border border-white/10 bg-slate-950/40 p-4">
        <Typography className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
          Fragmented
        </Typography>
        <Box className="mt-4 flex flex-wrap gap-2">
          {["Data", "Preprocess", "Analysis", "Interpretation"].map((item) => (
            <Box
              key={item}
              className="rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-center text-sm font-medium text-slate-200"
            >
              {item}
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="rounded-[24px] border border-cyan-400/20 bg-slate-950/40 p-4">
        <Typography className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">
          Integrated
        </Typography>
        <Box className="mt-4 rounded-2xl border border-cyan-400/25 bg-cyan-400/10 px-4 py-3 text-sm font-medium text-white">
          Data → Integration → Network → Insight
        </Box>
      </Box>
    </Box>
  </Box>
);

const Home = () => {
  const workflowsRef = useRef<HTMLDivElement | null>(null);
  const strengthsRef = useRef<HTMLDivElement | null>(null);
  const capabilitiesRef = useRef<HTMLDivElement | null>(null);
  const audienceRef = useRef<HTMLDivElement | null>(null);
  const contactRef = useRef<HTMLDivElement | null>(null);

  const scrollToSection = (sectionRef: SectionRef) => {
    sectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const workflows: Workflow[] = [
    {
      title: "Multi-Omics Analysis",
      description:
        "Integrate transcriptomic, proteomic, metabolomic, epigenetic, and related datasets in a shared biological context.",
      icon: BubbleChartRoundedIcon,
    },
    {
      title: "Biomarker Discovery",
      description:
        "Prioritize candidate biomarkers by linking differential signals with pathway context, network structure, and disease relevance.",
      icon: BiotechRoundedIcon,
    },
    {
      title: "Drug Target Exploration",
      description:
        "Investigate genes, pathways, and molecular interactions that may support therapeutic target evaluation.",
      icon: MedicationRoundedIcon,
    },
    {
      title: "Signature and Pathway Interpretation",
      description:
        "Interpret signatures, functional programs, and pathway activity across phenotypes, cohorts, and translational questions.",
      icon: QueryStatsRoundedIcon,
    },
  ];

  const capabilityGroups: CapabilityGroup[] = [
    {
      title: "Differential Analysis",
      icon: AutoGraphRoundedIcon,
      items: [
        "Differential gene expression",
        "Differential proteins / metabolites",
        "Differential methylation analysis",
      ],
    },
    {
      title: "Pathway and Functional Analysis",
      icon: ScienceRoundedIcon,
      items: [
        "KEGG / GO / Reactome enrichment",
        "Functional annotation",
        "Ontology mapping",
      ],
    },
    {
      title: "Network Analysis",
      icon: LanRoundedIcon,
      items: [
        "Gene co-expression networks",
        "Regulatory network inference",
        "Protein interaction networks",
        "Cross-omics relationship analysis",
      ],
    },
    {
      title: "Multi-Omics Integration",
      icon: BubbleChartRoundedIcon,
      items: [
        "Integration across omics layers",
        "Correlation and association structure",
        "Cohort-level comparative analysis",
      ],
    },
    {
      title: "Genome-Scale Metabolic Modeling",
      icon: SchemaRoundedIcon,
      items: [
        "Metabolic network analysis",
        "Flux-balance based systems interpretation",
        "Disease-associated metabolic rewiring exploration",
      ],
    },
    {
      title: "AI and Predictive Modeling",
      icon: PrecisionManufacturingRoundedIcon,
      items: [
        "Biomarker panel support",
        "Classification and risk modeling",
        "Pattern discovery across complex datasets",
      ],
    },
    {
      title: "Clinical and Translational Analytics",
      icon: QueryStatsRoundedIcon,
      items: [
        "Survival analysis",
        "Responder / non-responder analysis",
        "Disease subgroup exploration",
      ],
    },
  ];

  const audiences: Audience[] = [
    {
      title: "Academic Research Groups",
      description:
        "Support complex omics studies with a workflow that reduces technical overhead and improves interpretability.",
      icon: ScienceRoundedIcon,
    },
    {
      title: "Principal Investigators and Professors",
      description:
        "Give research programs a clearer path from complex data generation to biologically meaningful results.",
      icon: AccountTreeRoundedIcon,
    },
    {
      title: "Molecular Biology Researchers",
      description:
        "Connect molecular measurements to pathways, mechanisms, and disease-associated biology.",
      icon: BiotechRoundedIcon,
    },
    {
      title: "Systems Biology and Translational Teams",
      description:
        "Work across networks, pathways, and integrated omics layers to generate systems-level insight.",
      icon: DeviceHubRoundedIcon,
    },
    {
      title: "Biotech and Research-Driven Companies",
      description:
        "Support exploratory analysis and biological interpretation across discovery and translational programs.",
      icon: StorageRoundedIcon,
    },
  ];

  return (
    <Box className="flex h-full flex-col overflow-y-auto overflow-x-hidden bg-slate-950 text-slate-100 antialiased">
      <Box className="relative overflow-hidden">
        <Box
          className="pointer-events-none absolute inset-0"
          sx={{
            background:
              "radial-gradient(circle at 12% 18%, rgba(56,189,248,0.18), transparent 28%), radial-gradient(circle at 86% 12%, rgba(99,102,241,0.18), transparent 28%), linear-gradient(180deg, rgba(2,6,23,1) 0%, rgba(15,23,42,1) 58%, rgba(248,250,252,1) 100%)",
          }}
        />

        <Box className="relative mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6 md:pb-24 md:pt-16 lg:px-8 lg:pb-28">
          <Box className="grid gap-12 lg:grid-cols-[minmax(0,1.02fr)_minmax(360px,0.98fr)] lg:items-center">
            <Box className="max-w-3xl">
              <Box className="inline-flex items-center rounded-full border border-sky-400/20 bg-white/5 px-4 py-2 shadow-sm backdrop-blur">
                <Typography className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-200">
                  Multi-Omics and Systems Biology Platform
                </Typography>
              </Box>

              <Typography
                variant="h1"
                component="h1"
                className="mt-8 max-w-4xl text-5xl font-semibold leading-[0.98] text-white sm:text-6xl lg:text-7xl"
              >
                From preprocessing to inference in one multi-omics and systems
                biology platform
              </Typography>

              <Typography className="mt-8 max-w-3xl text-lg leading-8 text-slate-300 sm:text-xl">
                SurvOm is an automated multi-omics analysis and systems biology
                platform for molecular biology and translational research
                teams. It helps researchers move from preprocessing to
                inference, integrate multiple omics layers, perform network
                inference, and generate interpretable biological insight.
              </Typography>

              <Typography className="mt-5 text-sm font-medium leading-7 text-slate-400 sm:text-base">
                Built for research groups, scientists, and biotech teams
                working across complex biological data.
              </Typography>

              <Box className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => scrollToSection(workflowsRef)}
                  className="rounded-full px-8 py-3 text-base font-semibold"
                  sx={primaryButtonSx}
                >
                  Explore Platform
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection(capabilitiesRef)}
                  className="rounded-full px-8 py-3 text-base font-semibold"
                  sx={secondaryDarkButtonSx}
                >
                  View Capabilities
                </Button>
              </Box>

              <Box className="mt-12 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {[
                  "End-to-end workflow",
                  "Multi-omics integration",
                  "Network inference",
                  "Genome-scale metabolic modeling",
                  "Biomarker prioritization",
                ].map((item) => (
                  <Box
                    key={item}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 shadow-sm backdrop-blur"
                  >
                    <Typography className="text-sm font-medium leading-6 text-slate-200">
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <HeroWorkflowGraphic />
          </Box>
        </Box>
      </Box>

      <Box className="bg-slate-50 text-slate-900">
        <Box className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Box className="overflow-hidden rounded-[42px] border border-white/10 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 px-6 py-10 text-slate-100 shadow-2xl shadow-slate-300/20 sm:px-8 sm:py-12 lg:px-10">
            <Box className="grid gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(340px,0.92fr)] lg:items-center">
              <Box className="max-w-2xl">
                <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-300">
                  The Bottleneck
                </Typography>
                <Typography
                  variant="h2"
                  component="h2"
                  className="mt-5 text-4xl font-semibold leading-tight text-white"
                >
                  Turning multi-omics data into biological inference is still
                  fragmented
                </Typography>
                <Typography className="mt-5 max-w-xl text-lg leading-8 text-slate-300">
                  Multi-omics data is rich, but connecting heterogeneous
                  datasets to biological mechanisms remains slow and
                  fragmented.
                </Typography>

                <Box className="mt-8 space-y-4">
                  {[
                    {
                      title: "Fragmented workflows",
                      text: "preprocessing, integration, and interpretation are split across tools",
                    },
                    {
                      title: "High technical burden",
                      text: "complex pipelines slow scientific work",
                    },
                    {
                      title: "Limited interpretability",
                      text: "outputs do not clearly map to biology",
                    },
                    {
                      title: "Slow path to mechanism",
                      text: "difficult to connect data to disease insight",
                    },
                  ].map((item) => (
                    <Box key={item.title} className="flex items-start gap-4">
                      <Box className="mt-2.5 h-2.5 w-2.5 shrink-0 rounded-full bg-cyan-300" />
                      <Typography className="text-base leading-7 text-slate-300 sm:text-[1.02rem]">
                        <Box component="span" className="font-semibold text-white">
                          {item.title}
                        </Box>
                        {" — "}
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box className="lg:pl-4">
                <BottleneckMiniFlow />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        ref={workflowsRef}
        className="bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] text-slate-900"
      >
        <Box className="mx-auto max-w-7xl px-4 py-22 sm:px-6 lg:px-8">
          <Box className="mx-auto max-w-3xl text-center">
            <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
              Core Workflows
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              className="mt-5 text-4xl font-semibold text-slate-950"
            >
              What you can do with SurvOm
            </Typography>
            <Typography className="mt-5 text-lg leading-8 text-slate-600">
              Core workflows for multi-omics analysis, interpretation, and
              research support.
            </Typography>
          </Box>

          <Box className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {workflows.map((workflow) => {
              const Icon = workflow.icon;

              return (
                <Card
                  key={workflow.title}
                  className="group flex h-full flex-col rounded-[32px] border border-slate-200/80 bg-white/90 shadow-md shadow-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:border-sky-200 hover:shadow-xl hover:shadow-slate-200/80"
                >
                  <CardContent className="flex h-full flex-col p-8 md:p-9">
                    <Box className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-50 to-indigo-100 text-indigo-700 shadow-sm">
                      <Icon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography className="mt-6 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
                      Workflow
                    </Typography>
                    <Typography className="mt-3 text-2xl font-semibold leading-8 text-slate-950">
                      {workflow.title}
                    </Typography>
                    <Typography className="mt-4 flex-1 text-base leading-7 text-slate-600">
                      {workflow.description}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box ref={strengthsRef} className="bg-slate-950 text-slate-100">
        <Box className="mx-auto max-w-7xl px-4 py-22 sm:px-6 lg:px-8">
          <Box className="grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(340px,1.05fr)] lg:items-center">
            <Box className="max-w-2xl">
              <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-300">
                Scientific Strengths
              </Typography>
              <Typography
                variant="h2"
                component="h2"
                className="mt-5 text-4xl font-semibold leading-tight text-white"
              >
                Built on systems biology
              </Typography>
              <Typography className="mt-6 text-lg leading-8 text-slate-300">
                SurvOm connects molecular layers, network structure, and
                metabolic networks to support biological interpretation.
              </Typography>
              <Box className="mt-8 space-y-5">
                {[
                  {
                    title: "Multi-Omics Integration",
                    text: "Transcriptomics · Proteomics · Metabolomics · Epigenomics",
                  },
                  {
                    title: "Network Inference",
                    text: "Regulatory structure and cross-omics relationships",
                  },
                  {
                    title: "Genome-Scale Metabolic Modeling",
                    text: "Pathway behavior and metabolic rewiring",
                  },
                  {
                    title: "Systems-Level Interpretation",
                    text: "From molecular signals to disease mechanisms",
                  },
                ].map((item) => (
                  <Box key={item.title} className="flex items-start gap-4">
                    <Box className="mt-2.5 h-2.5 w-2.5 shrink-0 rounded-full bg-cyan-300" />
                    <Typography className="text-base leading-7 text-slate-300 sm:text-lg">
                      <Box component="span" className="font-semibold text-white">
                        {item.title}
                      </Box>
                      <br />
                      <Box component="span" className="text-slate-400">
                        {item.text}
                      </Box>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <ScientificStrengthsGraphic />
          </Box>
        </Box>
      </Box>

      <Box
        ref={capabilitiesRef}
        className="bg-[linear-gradient(180deg,#eff6ff_0%,#ffffff_100%)] text-slate-900"
      >
        <Box className="mx-auto max-w-7xl px-4 py-22 sm:px-6 lg:px-8">
          <Box className="mx-auto max-w-3xl text-center">
            <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
              Analytical Capabilities
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              className="mt-5 text-4xl font-semibold text-slate-950"
            >
              Analysis modules for molecular biology and translational research
            </Typography>
          </Box>

          <Box className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {capabilityGroups.map((group) => {
              const Icon = group.icon;

              return (
                <Card
                  key={group.title}
                  className="h-full rounded-[32px] border border-slate-200/80 bg-white/95 shadow-md shadow-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/80"
                >
                  <CardContent className="p-8 md:p-9">
                    <Box className="flex items-start gap-4">
                      <Box className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-50 to-indigo-100 text-indigo-700 shadow-sm">
                        <Icon sx={{ fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
                          Analysis Module
                        </Typography>
                        <Typography className="mt-2 text-xl font-semibold leading-8 text-slate-950">
                          {group.title}
                        </Typography>
                      </Box>
                    </Box>

                    <Box className="mt-6 space-y-3">
                      {group.items.map((item) => (
                        <Box
                          key={item}
                          className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                        >
                          <Box className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-sky-600" />
                          <Typography className="text-sm leading-6 text-slate-700">
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box ref={audienceRef} className="bg-slate-950 text-slate-100">
        <Box className="mx-auto max-w-7xl px-4 py-22 sm:px-6 lg:px-8">
          <Box className="mx-auto max-w-3xl text-center">
            <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-300">
              Who It&apos;s For
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              className="mt-5 text-4xl font-semibold text-white"
            >
              Built for the teams behind molecular discovery
            </Typography>
          </Box>

          <Box className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {audiences.map((audience) => {
              const Icon = audience.icon;

              return (
                <Card
                  key={audience.title}
                  className="h-full rounded-[30px] border border-white/10 bg-white/5 shadow-xl shadow-black/10 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07]"
                >
                  <CardContent className="p-8">
                    <Box className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-sky-200">
                      <Icon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography className="mt-6 text-lg font-semibold leading-7 text-white">
                      {audience.title}
                    </Typography>
                    <Typography className="mt-4 text-sm leading-7 text-slate-300">
                      {audience.description}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box ref={contactRef} className="bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] text-slate-900">
        <Box className="mx-auto max-w-7xl px-4 py-22 sm:px-6 lg:px-8">
          <Box className="mx-auto max-w-3xl rounded-[34px] border border-slate-200/80 bg-white p-8 shadow-xl shadow-slate-200/70 sm:p-10">
            <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
              Contact
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              className="mt-5 text-3xl font-semibold text-slate-950"
            >
              Contact
            </Typography>

            <Box className="mt-8 flex items-start gap-4">
              <Box className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-indigo-700 shadow-sm">
                <MailOutlineRoundedIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box className="space-y-2">
                <Typography className="text-lg font-semibold text-slate-950">
                  Vikash Pandey
                </Typography>
                <Typography className="text-base text-slate-600">
                  Umeå University, Sweden
                </Typography>
                <Typography
                  component="a"
                  href="mailto:vikash.pandey@umu.se"
                  className="block text-base font-medium text-indigo-700 underline decoration-indigo-200 underline-offset-4"
                >
                  vikash.pandey@umu.se
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className="bg-slate-950 text-slate-100">
        <Box className="mx-auto max-w-7xl px-4 py-22 sm:px-6 lg:px-8">
          <Box className="overflow-hidden rounded-[38px] border border-white/10 bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 p-8 shadow-2xl shadow-black/30 sm:p-10 lg:p-14">
            <Box className="mx-auto max-w-3xl text-center">
              <Typography className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-200">
                Explore SurvOm
              </Typography>
              <Typography
                variant="h2"
                component="h2"
                className="mt-5 text-4xl font-semibold leading-tight text-white md:text-5xl"
              >
                Explore SurvOm by workflow or capability
              </Typography>
              <Typography className="mt-6 text-lg leading-8 text-slate-300">
                Review the platform structure, then move to the analysis modules
                that fit your study design and research question.
              </Typography>

              <Box className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => scrollToSection(workflowsRef)}
                  className="rounded-full px-8 py-3 text-base font-semibold"
                  sx={primaryButtonSx}
                >
                  Explore Platform
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection(capabilitiesRef)}
                  className="rounded-full px-8 py-3 text-base font-semibold"
                  sx={secondaryDarkButtonSx}
                >
                  View Capabilities
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
